import React, { useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { casketsAPI, casketSalesAPI, clientsAPI } from '../services/api';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArchiveBoxIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import {
  LoadingSpinner,
  Button,
  Modal,
  Card,
  Input,
  ConfirmModal,
  CoffinSaleCard,
  QuickSellModal,
  StockIndicator
} from '../components/ui';
import type { SaleData } from '../components/ui/QuickSellModal';
import { Coffin, CasketSale } from '../types';
import toast from 'react-hot-toast';

type TabId = 'sales' | 'inventory' | 'history';

interface CoffinFormData {
  name: string;
  model: string;
  description: string;
  material: string;
  size: string;
  price: number;
  stock: number;
  isRental: boolean;
  isAvailable: boolean;
}

const MODEL_OPTIONS = [
  { value: 'CLASSIC', label: 'Clásico' },
  { value: 'MODERN', label: 'Moderno' },
  { value: 'WOOD', label: 'Madera' },
  { value: 'METAL', label: 'Metal' },
  { value: 'RENTAL_CLASSIC', label: 'Renta clásico' },
  { value: 'TEST_LUXURY', label: 'Lujo' }
];

const MATERIAL_LABELS: Record<string, string> = {
  wood: 'Madera',
  metal: 'Metal',
  fiber: 'Fibra',
  other: 'Otro'
};

const SIZE_LABELS: Record<string, string> = {
  standard: 'Estándar',
  large: 'Grande',
  child: 'Infantil',
  premium: 'Premium'
};

const PAYMENT_LABELS: Record<string, string> = {
  EFECTIVO: 'Efectivo',
  TRANSFERENCIA_BANCARIA: 'Transferencia bancaria',
  TRANSFERENCIA_MOVIL: 'Transferencia móvil',
  CHEQUE: 'Cheque',
  DEPOSITO_BANCARIO: 'Depósito bancario'
};

function normalizeCoffin(raw: any): Coffin {
  return {
    id: raw.id,
    name: raw.name || raw.model || `Ataúd #${raw.id}`,
    model: raw.model,
    description: raw.description || '',
    material: raw.material || 'wood',
    size: raw.size || 'standard',
    price: Number(raw.price) || 0,
    stock: Number(raw.stock) || 0,
    isRental: Boolean(raw.isRental),
    isAvailable: raw.isAvailable !== false,
    inventoryDate: raw.inventoryDate,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt
  };
}

const CoffinModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  coffin?: Coffin | null;
  onSave: (data: CoffinFormData) => void;
  isLoading?: boolean;
}> = ({ isOpen, onClose, coffin, onSave, isLoading = false }) => {
  const { register, handleSubmit, formState: { errors }, reset } = useForm<CoffinFormData>({
    defaultValues: coffin
      ? {
          name: coffin.name,
          model: coffin.model || 'CLASSIC',
          description: coffin.description || '',
          material: coffin.material,
          size: coffin.size,
          price: coffin.price,
          stock: coffin.stock,
          isRental: Boolean(coffin.isRental),
          isAvailable: coffin.isAvailable
        }
      : {
          name: '',
          model: 'CLASSIC',
          description: '',
          material: 'wood',
          size: 'standard',
          price: 0,
          stock: 1,
          isRental: false,
          isAvailable: true
        }
  });

  React.useEffect(() => {
    if (isOpen) {
      reset(
        coffin
          ? {
              name: coffin.name,
              model: coffin.model || 'CLASSIC',
              description: coffin.description || '',
              material: coffin.material,
              size: coffin.size,
              price: coffin.price,
              stock: coffin.stock,
              isRental: Boolean(coffin.isRental),
              isAvailable: coffin.isAvailable
            }
          : {
              name: '',
              model: 'CLASSIC',
              description: '',
              material: 'wood',
              size: 'standard',
              price: 0,
              stock: 1,
              isRental: false,
              isAvailable: true
            }
      );
    }
  }, [isOpen, coffin, reset]);

  const onSubmit = (data: CoffinFormData) => {
    onSave({
      ...data,
      price: Number(data.price),
      stock: Number(data.stock)
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={coffin ? 'Editar ataúd (SKU)' : 'Nuevo ataúd (SKU)'}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Nombre"
            placeholder="Nombre comercial"
            error={errors.name?.message}
            required
            {...register('name', { required: 'El nombre es requerido' })}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Modelo / categoría</label>
            <select
              {...register('model', { required: true })}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              {MODEL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Material</label>
            <select {...register('material')} className="block w-full px-3 py-2 border border-gray-300 rounded-lg">
              <option value="wood">Madera</option>
              <option value="metal">Metal</option>
              <option value="fiber">Fibra</option>
              <option value="other">Otro</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tamaño</label>
            <select {...register('size')} className="block w-full px-3 py-2 border border-gray-300 rounded-lg">
              <option value="standard">Estándar</option>
              <option value="large">Grande</option>
              <option value="child">Infantil</option>
              <option value="premium">Premium</option>
            </select>
          </div>
          <Input
            label="Precio"
            type="number"
            step="0.01"
            error={errors.price?.message}
            required
            {...register('price', { required: 'El precio es requerido', min: { value: 0.01, message: 'Precio inválido' } })}
          />
          <Input
            label="Stock"
            type="number"
            error={errors.stock?.message}
            required
            {...register('stock', { required: 'El stock es requerido', min: { value: 0, message: 'Stock inválido' } })}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
          <textarea
            {...register('description')}
            rows={3}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="Descripción opcional"
          />
        </div>
        <div className="flex flex-wrap gap-6">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" {...register('isRental')} className="rounded border-gray-300" />
            Es de renta
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" {...register('isAvailable')} className="rounded border-gray-300" />
            SKU activo / vendible
          </label>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button type="submit" loading={isLoading}>{coffin ? 'Guardar' : 'Crear'}</Button>
        </div>
      </form>
    </Modal>
  );
};

const Coffins: React.FC = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabId>('sales');
  const [searchTerm, setSearchTerm] = useState('');
  const [materialFilter, setMaterialFilter] = useState('');
  const [sizeFilter, setSizeFilter] = useState('');
  const [stockFilter, setStockFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCoffin, setEditingCoffin] = useState<Coffin | null>(null);
  const [deletingCoffin, setDeletingCoffin] = useState<Coffin | null>(null);
  const [sellingCoffin, setSellingCoffin] = useState<Coffin | null>(null);
  const [historySearch, setHistorySearch] = useState('');

  const { data: casketsRaw, isLoading } = useQuery(
    ['caskets', materialFilter, sizeFilter, stockFilter],
    () =>
      casketsAPI.getAll({
        material: materialFilter || undefined,
        size: sizeFilter || undefined,
        stockStatus: stockFilter || undefined
      }),
    { select: (res) => (res.data?.data || []).map(normalizeCoffin) }
  );

  const { data: stats } = useQuery(['casket-stats'], () => casketsAPI.getStats(), {
    select: (res) => res.data?.data
  });

  const { data: clients = [] } = useQuery(
    ['clients-for-sales'],
    () => clientsAPI.getAll({ limit: 200 }),
    {
      select: (res) => {
        const list = res.data?.data || res.data || [];
        return Array.isArray(list) ? list : [];
      }
    }
  );

  const { data: sales = [], isLoading: salesLoading } = useQuery(
    ['casket-sales', historySearch],
    () => casketSalesAPI.getAll({ search: historySearch || undefined }),
    {
      enabled: activeTab === 'history',
      select: (res) => (res.data?.data || []) as CasketSale[]
    }
  );

  const coffins = casketsRaw || [];

  const filteredCoffins = useMemo(() => {
    if (!searchTerm.trim()) return coffins;
    const q = searchTerm.toLowerCase();
    return coffins.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.model || '').toLowerCase().includes(q) ||
        (c.description || '').toLowerCase().includes(q) ||
        c.material.toLowerCase().includes(q) ||
        c.size.toLowerCase().includes(q)
    );
  }, [coffins, searchTerm]);

  const sellableCoffins = useMemo(
    () => filteredCoffins.filter((c) => !c.isRental && c.isAvailable && c.stock > 0),
    [filteredCoffins]
  );

  const createMutation = useMutation((data: CoffinFormData) => casketsAPI.create(data), {
    onSuccess: () => {
      queryClient.invalidateQueries('caskets');
      queryClient.invalidateQueries('casket-stats');
      toast.success('Ataúd creado');
      setIsModalOpen(false);
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Error al crear')
  });

  const updateMutation = useMutation(
    ({ id, data }: { id: string | number; data: CoffinFormData }) => casketsAPI.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('caskets');
        queryClient.invalidateQueries('casket-stats');
        toast.success('Ataúd actualizado');
        setIsModalOpen(false);
        setEditingCoffin(null);
      },
      onError: (err: any) => toast.error(err?.response?.data?.message || 'Error al actualizar')
    }
  );

  const deleteMutation = useMutation((id: string | number) => casketsAPI.delete(id), {
    onSuccess: () => {
      queryClient.invalidateQueries('caskets');
      queryClient.invalidateQueries('casket-stats');
      toast.success('Ataúd eliminado');
      setDeletingCoffin(null);
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Error al eliminar')
  });

  const saleMutation = useMutation(
    (payload: SaleData & { casketId: number }) => casketSalesAPI.create(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('caskets');
        queryClient.invalidateQueries('casket-stats');
        queryClient.invalidateQueries('casket-sales');
        toast.success('Venta registrada');
      }
    }
  );

  const handleSave = (data: CoffinFormData) => {
    if (editingCoffin) {
      updateMutation.mutate({ id: editingCoffin.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleProcessSale = async (data: SaleData) => {
    if (!sellingCoffin) return;
    const res = await saleMutation.mutateAsync({
      ...data,
      casketId: Number(sellingCoffin.id)
    });
    const sale = res.data?.data;
    return { id: sale.id, paymentId: sale.paymentId };
  };

  const handleDownloadReceipt = async (saleId: number) => {
    const res = await casketSalesAPI.downloadReceipt(saleId);
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `recibo_venta_ataud_${saleId}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Recibo descargado');
  };

  const clearFilters = () => {
    setSearchTerm('');
    setMaterialFilter('');
    setSizeFilter('');
    setStockFilter('');
  };

  const tabs: { id: TabId; label: string }[] = [
    { id: 'sales', label: 'Ventas' },
    { id: 'inventory', label: 'Inventario' },
    { id: 'history', label: 'Historial' }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ataúdes</h1>
          <p className="text-gray-600">Ventas POS e inventario por SKU</p>
        </div>
        {activeTab === 'inventory' && (
          <Button
            onClick={() => {
              setEditingCoffin(null);
              setIsModalOpen(true);
            }}
          >
            <PlusIcon className="h-5 w-5 mr-1 inline" />
            Nuevo ataúd
          </Button>
        )}
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-4">
            <p className="text-xs text-gray-500">SKUs</p>
            <p className="text-xl font-semibold">{stats.totalSkus ?? stats.totalCaskets ?? 0}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-gray-500">Unidades en stock</p>
            <p className="text-xl font-semibold">{stats.totalUnitsInStock ?? 0}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-gray-500">Stock bajo</p>
            <p className="text-xl font-semibold text-amber-700">{stats.lowStockSkus ?? 0}</p>
          </Card>
          <Card className="p-4">
            <p className="text-xs text-gray-500">Valor inventario</p>
            <p className="text-xl font-semibold">
              ${Number(stats.inventoryValue ?? stats.totalValue ?? 0).toLocaleString()}
            </p>
          </Card>
        </div>
      )}

      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={clsx(
                'pb-3 text-sm font-medium border-b-2 -mb-px',
                activeTab === tab.id
                  ? 'border-gray-900 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {(activeTab === 'sales' || activeTab === 'inventory') && (
        <Card className="p-4">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por nombre, material, tamaño…"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <select
              value={materialFilter}
              onChange={(e) => setMaterialFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">Material</option>
              <option value="wood">Madera</option>
              <option value="metal">Metal</option>
              <option value="fiber">Fibra</option>
              <option value="other">Otro</option>
            </select>
            <select
              value={sizeFilter}
              onChange={(e) => setSizeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">Tamaño</option>
              <option value="standard">Estándar</option>
              <option value="large">Grande</option>
              <option value="child">Infantil</option>
              <option value="premium">Premium</option>
            </select>
            <select
              value={stockFilter}
              onChange={(e) => setStockFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">Stock</option>
              <option value="in_stock">Con stock</option>
              <option value="low_stock">Stock bajo</option>
              <option value="out_of_stock">Agotados</option>
            </select>
            <Button variant="secondary" onClick={clearFilters}>Limpiar</Button>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            {activeTab === 'sales'
              ? `${sellableCoffins.length} disponibles para venta`
              : `${filteredCoffins.length} SKU(s)`}
          </p>
        </Card>
      )}

      {activeTab === 'sales' && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {sellableCoffins.map((coffin) => (
            <CoffinSaleCard
              key={coffin.id}
              coffin={coffin}
              onSell={() => setSellingCoffin(coffin)}
              onEdit={() => {
                setEditingCoffin(coffin);
                setIsModalOpen(true);
                setActiveTab('inventory');
              }}
              onDelete={() => setDeletingCoffin(coffin)}
              badge={coffin.stock <= 2 ? 'limited' : null}
            />
          ))}
          {sellableCoffins.length === 0 && (
            <Card className="col-span-full p-10 text-center text-gray-500">
              No hay ataúdes de venta con stock disponible.
            </Card>
          )}
        </div>
      )}

      {activeTab === 'inventory' && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredCoffins.map((coffin) => (
            <Card key={coffin.id} className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 min-w-0">
                  <ArchiveBoxIcon className="h-5 w-5 text-gray-400 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500">ID: {coffin.id}</p>
                    <h3 className="font-semibold text-gray-900 truncate">{coffin.name}</h3>
                    <p className="text-xs text-gray-500">{coffin.model}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingCoffin(coffin);
                      setIsModalOpen(true);
                    }}
                    className="p-1.5 text-gray-500 hover:text-gray-900"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeletingCoffin(coffin)}
                    className="p-1.5 text-gray-500 hover:text-red-600"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="text-sm space-y-1">
                <p>
                  <span className="text-gray-500">Material:</span>{' '}
                  {MATERIAL_LABELS[coffin.material] || coffin.material}
                </p>
                <p>
                  <span className="text-gray-500">Tamaño:</span>{' '}
                  {SIZE_LABELS[coffin.size] || coffin.size}
                </p>
                <p className="text-lg font-semibold">${Number(coffin.price).toLocaleString()}</p>
                <div className="flex items-center justify-between">
                  <StockIndicator current={coffin.stock} showBadge size="sm" />
                  <div className="flex gap-1">
                    {coffin.isRental && (
                      <span className="px-2 py-0.5 rounded-full text-[11px] bg-blue-50 text-blue-800">Renta</span>
                    )}
                    <span
                      className={clsx(
                        'px-2 py-0.5 rounded-full text-[11px]',
                        coffin.isAvailable ? 'bg-emerald-50 text-emerald-800' : 'bg-gray-100 text-gray-600'
                      )}
                    >
                      {coffin.isAvailable ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                </div>
              </div>
            </Card>
          ))}
          {filteredCoffins.length === 0 && (
            <Card className="col-span-full p-10 text-center text-gray-500">
              No hay SKUs que coincidan con los filtros.
            </Card>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <Card className="overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <div className="relative max-w-md">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={historySearch}
                onChange={(e) => setHistorySearch(e.target.value)}
                placeholder="Buscar ventas…"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
          {salesLoading ? (
            <div className="p-10 flex justify-center"><LoadingSpinner /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-gray-600">
                  <tr>
                    <th className="px-4 py-3">#</th>
                    <th className="px-4 py-3">Fecha</th>
                    <th className="px-4 py-3">Ataúd</th>
                    <th className="px-4 py-3">Comprador</th>
                    <th className="px-4 py-3">Cant.</th>
                    <th className="px-4 py-3">Total</th>
                    <th className="px-4 py-3">Pago</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sales.map((sale) => (
                    <tr key={sale.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{sale.id}</td>
                      <td className="px-4 py-3">
                        {new Date(sale.soldAt).toLocaleString('es')}
                      </td>
                      <td className="px-4 py-3">{sale.casket?.name || `#${sale.casketId}`}</td>
                      <td className="px-4 py-3">
                        {sale.client
                          ? `${sale.client.name} ${sale.client.lastname || ''}`.trim()
                          : sale.walkInName || '—'}
                      </td>
                      <td className="px-4 py-3">{sale.quantity}</td>
                      <td className="px-4 py-3 font-semibold">
                        ${Number(sale.totalAmount).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        {PAYMENT_LABELS[sale.paymentMethod] || sale.paymentMethod}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => handleDownloadReceipt(sale.id)}
                          className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900"
                          title="Descargar recibo"
                        >
                          <ArrowDownTrayIcon className="h-4 w-4" />
                          Recibo
                        </button>
                      </td>
                    </tr>
                  ))}
                  {sales.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-10 text-center text-gray-500">
                        Aún no hay ventas registradas.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      <CoffinModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingCoffin(null);
        }}
        coffin={editingCoffin}
        onSave={handleSave}
        isLoading={createMutation.isLoading || updateMutation.isLoading}
      />

      <ConfirmModal
        isOpen={Boolean(deletingCoffin)}
        onClose={() => setDeletingCoffin(null)}
        onConfirm={() => deletingCoffin && deleteMutation.mutate(deletingCoffin.id)}
        title="Eliminar ataúd"
        message={`¿Eliminar "${deletingCoffin?.name}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        type="danger"
        isLoading={deleteMutation.isLoading}
      />

      <QuickSellModal
        isOpen={Boolean(sellingCoffin)}
        coffin={sellingCoffin}
        clients={clients}
        onConfirm={handleProcessSale}
        onDownloadReceipt={handleDownloadReceipt}
        onCancel={() => setSellingCoffin(null)}
      />
    </div>
  );
};

export default Coffins;
